import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  Plus,
  AlertCircle,
  TrendingUp,
  Calendar,
  Receipt,
  X,
  CheckCircle
} from 'lucide-react'
import { getIncome, recordIncomePayment } from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'
import { renderDate } from '../../utils/time'

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-UG', {
    style: 'currency',
    currency: 'UGX',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount)
}

export default function IncomeList({ ranchId }) {
  const queryClient = useQueryClient()
  const { ranchTimezone, canWrite } = useAuth()
  const [page, setPage] = useState(1)
  const [paymentModal, setPaymentModal] = useState(null)

  const { data: income, isLoading, error } = useQuery({
    queryKey: ['income', ranchId, page],
    queryFn: () => getIncome(ranchId, { page, page_size: 20 }),
  })

  if (error) {
    return (
      <div className="card p-6 text-center">
        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900">Failed to load income</h3>
        <p className="text-gray-500 mt-1">{error.message}</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Income</h1>
          <p className="text-gray-500 mt-1">Track and manage ranch income</p>
        </div>
        {canWrite && (
          <Link to="/income/new" className="btn bg-green-600 text-white hover:bg-green-700 flex items-center">
            <Plus className="h-4 w-4 mr-2" />
            Add Income
          </Link>
        )}
      </div>

      {/* Income List */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : income?.items?.length === 0 ? (
          <div className="text-center py-12">
            <TrendingUp className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900">No income recorded</h3>
            <p className="text-gray-500 mt-1">Record your first income to get started</p>
          </div>
        ) : (
          <>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Buyer
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Payment
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {income?.items?.map((record) => (
                  <tr key={record.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        {renderDate(record.income_date, ranchTimezone)}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">
                        {record.description}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                        {record.income_type?.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {record.buyer_name || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-700 text-right">
                      {formatCurrency(record.amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {record.payment_status === 'paid' ? (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                          paid
                        </span>
                      ) : canWrite ? (
                        <button
                          onClick={() => setPaymentModal(record)}
                          className="px-2 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-800 hover:bg-amber-200 cursor-pointer"
                        >
                          unpaid - record
                        </button>
                      ) : (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-800">
                          unpaid
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {income && income.pages > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
                <div className="text-sm text-gray-700">
                  Page {income.page} of {income.pages}
                </div>
                <div className="flex gap-2">
                  <button
                    className="btn-secondary"
                    disabled={page === 1}
                    onClick={() => setPage(p => p - 1)}
                  >
                    Previous
                  </button>
                  <button
                    className="btn-secondary"
                    disabled={page >= income.pages}
                    onClick={() => setPage(p => p + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Record Payment Modal */}
      {paymentModal && (
        <RecordIncomePaymentModal
          record={paymentModal}
          onClose={() => setPaymentModal(null)}
          onSuccess={() => {
            setPaymentModal(null)
            queryClient.invalidateQueries(['income'])
          }}
        />
      )}
    </div>
  )
}

function RecordIncomePaymentModal({ record, onClose, onSuccess }) {
  const { userId } = useAuth()
  const [formData, setFormData] = useState({
    payment_reference: '',
    payment_method: 'mobile_money',
    counterparty_msisdn: record.payer_msisdn || '',
    proof_photo: null,
    recorded_by: userId,
  })
  const [error, setError] = useState('')

  const mutation = useMutation({
    mutationFn: (data) => recordIncomePayment(record.id, data),
    onSuccess: () => onSuccess(),
    onError: (err) => setError(err.response?.data?.detail || 'Failed to record payment'),
  })

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0]
    if (file) {
      const reader = new FileReader()
      reader.onload = (e) => setFormData(prev => ({ ...prev, proof_photo: e.target.result }))
      reader.readAsDataURL(file)
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    setError('')
    if (!formData.payment_reference.trim()) {
      setError('Payment reference is required')
      return
    }
    mutation.mutate(formData)
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">Record Payment Received</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mb-4 p-3 bg-gray-50 rounded-lg">
          <p className="text-sm text-gray-600">{record.description}</p>
          <p className="text-lg font-bold text-green-700 mt-1">
            {formatCurrency(record.amount)}
          </p>
          {record.buyer_name && (
            <p className="text-sm text-gray-500">From: {record.buyer_name}</p>
          )}
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              MoMo Reference Number *
            </label>
            <input
              type="text"
              required
              value={formData.payment_reference}
              onChange={(e) => setFormData({ ...formData, payment_reference: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              placeholder="e.g. MTN-TXN-20260210-001"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Payer Phone Number
            </label>
            <input
              type="tel"
              value={formData.counterparty_msisdn}
              onChange={(e) => setFormData({ ...formData, counterparty_msisdn: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              placeholder="256XXXXXXXXX"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Payment Method
            </label>
            <select
              value={formData.payment_method}
              onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
            >
              <option value="mobile_money">Mobile Money</option>
              <option value="cash">Cash</option>
              <option value="bank">Bank Transfer</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Proof Photo (optional)
            </label>
            {formData.proof_photo ? (
              <div className="relative inline-block">
                <img src={formData.proof_photo} alt="Proof" className="h-24 w-auto rounded-lg border" />
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, proof_photo: null })}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ) : (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-3">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  className="hidden"
                  id="income-proof-upload"
                />
                <label htmlFor="income-proof-upload" className="flex flex-col items-center cursor-pointer">
                  <Receipt className="h-6 w-6 text-gray-400 mb-1" />
                  <span className="text-sm text-gray-600">Upload MoMo screenshot</span>
                </label>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              <CheckCircle className="h-4 w-4" />
              {mutation.isPending ? 'Recording...' : 'Confirm Payment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
